import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { IConversationCustomer } from '../../../models/conversation.models';

@Component({
  selector: 'app-customer-association-menu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, TranslateModule, UiModule],
  templateUrl: './customer-association-menu.component.html',
  styleUrls: ['./customer-association-menu.component.scss'],
})
export class CustomerAssociationMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() selectedCustomerId: number | null = null;
  @Input() updateType: 'single' | 'bulk' = 'single';
  /**
   * Whether to offer "Remove association". Callers that open this picker where
   * nothing is linked yet (e.g. the sidebar's empty state) pass false, since
   * there is nothing to remove.
   */
  @Input() showRemove = true;

  /** Emits the selected location id and display name so parent can set conversation customer without the full list */
  @Output() customerSelected = new EventEmitter<IConversationCustomer>();
  @Output() associationRemoved = new EventEmitter<void>();

  searchControl = new FormControl('', { nonNullable: true });

  /** Locations loaded from API (accumulated across pages) */
  locationsList: ILocation[] = [];
  page = 1;
  totalPages = 1;
  readonly pageSize = 5;
  loading = false;
  private readonly scrollThreshold = 80;

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  constructor(private customersService: CustomersService) {}

  ngOnInit(): void {
    this.loadPage(1, false);

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.loadPage(1, false, term.trim() || undefined));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasMoreToLoad(): boolean {
    return this.page < this.totalPages && !this.loading;
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || !this.hasMoreToLoad) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - this.scrollThreshold) {
      this.loadPage(this.page + 1, true, this.searchControl.value.trim() || undefined);
    }
  }

  /** Display label: company_name + location name, or just location name */
  getLocationDisplayName(location: ILocation): string {
    const name = location.name ?? '';
    const company = location.company_name?.trim();
    return company ? `${company} – ${name}` : name;
  }

  private loadPage(
    pageNum: number,
    append: boolean,
    searchKey?: string
  ): void {
    if (this.loading) return;
    this.loading = true;

    this.customersService
      .listLocations(
        { number: pageNum, size: this.pageSize },
        undefined,
        searchKey ?? undefined,
        'name',
        'asc'
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const data = res.data ?? [];
          const meta = res.metadata;
          this.totalPages = meta?.pages ?? 1;
          this.page = pageNum;

          if (append) {
            const existingIds = new Set(this.locationsList.map((l) => l.id));
            const newOnes = data.filter((l) => l.id != null && !existingIds.has(l.id));
            this.locationsList = [...this.locationsList, ...newOnes];
          } else {
            this.locationsList = data;
          }
        },
        error: () => {
          if (!append) this.locationsList = [];
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  selectCustomer(location: ILocation): void {
    if (location.id != null) {
      this.customerSelected.emit({
        id: location.id,
        company_name: location.company_name ?? '',
        location: location.name ?? '',
      });
    }
  }

  removeAssociation(): void {
    this.associationRemoved.emit();
  }

  isSelected(location: ILocation): boolean {
    return this.selectedCustomerId != null && location.id === this.selectedCustomerId;
  }

  trackByLocationId(_index: number, location: ILocation): number | undefined {
    return location.id;
  }
}
